import { useEffect, useRef } from "react";
import { HeaderMenu } from "./features/headermenu/DashHeaderMenu";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import { grey } from "@mui/material/colors";
import Library from "./features/library/Library";
import FunctionMapModal from "./features/library/FunctionMapModal";
import RepoNetworkModal from "./features/library/RepoNetworkModal";

import useActions, { useAppDispatch, useAppSelector } from "./app/hooks";
import { selectEditingDash } from "./features/editor/dashEditorSlice";
import { logout, selectAuth, selectUser } from "./features/auth/authSlice";
import { setOnUnauthorized } from "./services/client";
import { selectNavLocation } from "./features/navigation/navigationSlice";
import { openNetwork, setNetworkMode, selectRepos } from "./features/library/librarySlice";

import DashEditorContainer from "./features/editor/DashEditorContainer";
import SignInModal from './features/auth/SignInModal';
import ResetModal from './features/auth/ResetModal';
import ChangePasswordModal from './features/auth/ChangePasswordModal';
import AgentIsland from './features/agents/AgentIsland';

import "./App.css";

function getParameterByName(name:string, url = window.location.href) {
  name = name.replace(/[[\]]/g, '\\$&');
  const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
      results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

function App() {
  const actions = useActions();
  const dispatch = useAppDispatch();
  const authState = useAppSelector(selectAuth);

  const userData = useAppSelector(selectUser);
  //TODO: Implement a better state management
  const selectedDash = useAppSelector(selectEditingDash);

  useEffect(() => {
    actions.loadIdeState();
  }, []);

  // Register 401 handler so client.js can trigger logout without circular dependency
  useEffect(() => {
    setOnUnauthorized(() => dispatch(logout()));
    return () => setOnUnauthorized(null);
  }, [dispatch]);

  // Restore session: when user is in state (from localStorage) but organizations not loaded, fetch them
  useEffect(() => {
    if (userData != null && authState.organizations == null && authState.status === "logged-in") {
      actions.getOrganizations();
    }
  }, [userData, authState.organizations, authState.status]);

  // Restore navigation on refresh: re-open the graph at the saved location once
  // the user + repos are loaded. Falls back to the library if the saved repo is
  // gone (or the saved location wasn't a graph view).
  const navLocation = useAppSelector(selectNavLocation);
  const repos = useAppSelector(selectRepos);
  const navRestoredRef = useRef(false);
  useEffect(() => {
    if (navRestoredRef.current) return;
    if (userData == null || repos == null || repos.length === 0) return;
    navRestoredRef.current = true;
    const loc = navLocation;
    if (loc.kind === "graph" && loc.networkRepoId && repos.some((r) => r.id === loc.networkRepoId)) {
      const repoId = loc.networkRepoId;
      dispatch(openNetwork({ repoId }));
      if (loc.networkMode) dispatch(setNetworkMode(loc.networkMode));
      actions.loadGraphSettings({ repoId });
      actions.loadFileGraph({ repoId });
      actions.pollIndexStatus({ repoId, untilDone: true });
    }
    // else: missing/stale graph location → fall back to the library (default view)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, repos]);

  //console.log("A", selectedDash);

  // For password reset
  if(window.location.pathname == "/reset") {
    const token = getParameterByName('token');

    if (token !== null) {
      return (<ResetModal authState={authState} token={token}></ResetModal>)
    }
    else {
      (window as any).location = "/";
    }
  }

  // Standalone island test for the agent modal (no auth / redux required).
  if (window.location.pathname === "/agent-island") {
    return <AgentIsland />;
  }

  // External viewing
  /*
  if(window.location.pathname.split("/")[1] == "dash_outside") {
    const result = useEffect(() => {
      //const selectedDash = useAppSelector(selectEditingDash);

      actions.setEditingDash({
        id: parseInt(window.location.pathname.split("/")[2]),
        user_id: -1,
        created_at: "",
        updated_at: "",
        deleted_at: "",
        terms: "",
        name: "",
        parent_folder_id: -1,
        dashes_roles: [],
      });
    }, []);

    return (
      <Box sx={{bgColor: grey[200]}}>
        <CssBaseline />
        <DashEditorContainer from_outside={true}/>
      </Box>);
  }*/

  if(authState.status === "logged-out") {
    return (<SignInModal authState={authState} open={true}></SignInModal>)
  }
  
  if(authState.status === "change-password") {
    if (userData !== null) {
      return (<ChangePasswordModal email={ userData.email} authState={authState}></ChangePasswordModal>)
    }
  }

  const library =
    authState.status === "logged-in" ? (
      <Box >
        <Library></Library>
      </Box>
    ) : null;

  return (
    <Box sx={{bgColor: grey[200]}}>
      <CssBaseline />
      <HeaderMenu></HeaderMenu>
      {selectedDash !== null ? <DashEditorContainer  from_outside={false}/> : library}
      <FunctionMapModal />
      <RepoNetworkModal />
    </Box>
  );
}

export default App;
